/**
 * Chutt... - MissionCard
 * Card displaying a mission with gauge, icon, sub-objectives, and ChartExplainer
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  LinearProgress,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupsIcon from '@mui/icons-material/Groups';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import MissionGauge from './MissionGauge';
import ChartExplainer from './ChartExplainer';
import { colors } from '../../styles/colors';

const MISSION_ICONS = {
  shield: ShieldIcon,
  campaign: CampaignIcon,
  handshake: HandshakeIcon,
  groups: GroupsIcon,
};

const MISSION_CHART_IDS = {
  protect: 'missionProtect',
  visibility: 'missionVisibility',
  trust: 'missionTrust',
  community: 'missionCommunity',
};

const getScoreColor = (score) => {
  if (score >= 70) return colors.success;
  if (score >= 40) return colors.warning;
  return colors.danger;
};

function MissionCard({
  missionKey,
  name,
  tagline,
  score = 0,
  icon = 'shield',
  subObjectives = [],
  expanded = false,
  onToggleExpand,
  variant = 'default',
  loading = false,
}) {
  const IconComponent = MISSION_ICONS[icon] || ShieldIcon;
  const scoreColor = getScoreColor(score);
  const chartId = MISSION_CHART_IDS[missionKey];
  const isCompact = variant === 'compact';

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="circular" width={isCompact ? 70 : 100} height={isCompact ? 70 : 100} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="80%" height={18} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </Paper>
    );
  }

  const cardContent = (
    <Paper
      elevation={0}
      sx={{
        p: isCompact ? 1.5 : 3,
        borderRadius: '12px',
        border: `1px solid ${colors.gray200}`,
        bgcolor: colors.white,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 16px ${scoreColor}20`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isCompact ? 1.5 : 2 }}>
        {/* Gauge */}
        <MissionGauge score={score} size={isCompact ? 70 : 100} label={name} />

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
            <IconComponent sx={{ fontSize: isCompact ? 16 : 20, color: scoreColor }} />
            <Typography
              variant={isCompact ? 'body2' : 'subtitle1'}
              sx={{ fontWeight: 600, fontFamily: '"Playfair Display", Georgia, serif', color: colors.charcoal }}
            >
              {name}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{ color: colors.gray500, display: 'block', mb: 0.5 }}
          >
            {tagline}
          </Typography>

          <Chip
            label={`${Math.round(score)}%`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: `${scoreColor}18`,
              color: scoreColor,
              border: `1px solid ${scoreColor}40`,
            }}
          />
        </Box>

        {/* Expand button (not in compact mode) */}
        {!isCompact && onToggleExpand && (
          <IconButton size="small" onClick={onToggleExpand} aria-label={expanded ? 'Replier' : 'Developper'}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Sub-objectives collapse */}
      {!isCompact && (
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${colors.gray100}` }}>
            {subObjectives.map((obj, idx) => (
              <Box key={idx} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: colors.gray600, fontSize: '0.72rem' }}>
                    {obj.name}
                    <Typography component="span" sx={{ color: colors.gray400, fontSize: '0.65rem', ml: 0.5 }}>
                      (x{obj.weight?.toFixed(2)})
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.charcoal, fontSize: '0.72rem' }}>
                    {Math.round(obj.current || 0)} / {Math.round(obj.target || 0)} {obj.unit}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, obj.progress_pct || 0)}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: colors.gray100,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: getScoreColor(obj.progress_pct || 0),
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Paper>
  );

  // Wrap with ChartExplainer if chartId is available
  if (chartId) {
    return (
      <ChartExplainer chartId={chartId}>
        {cardContent}
      </ChartExplainer>
    );
  }

  return cardContent;
}

export default MissionCard;
